import { Component, createRef } from "react";

import type { IComponent } from "@Components/Component";
import { defaultAttributes } from "@Components/Component";
import { Modal } from "@Components/Modal";
import { requestTrack } from "@Components/Modules/Helpers";

import type { IUserCommunicationTemplate } from "./UserCommunicationTemplate";
import { DisplayType } from "./UserCommunicationTemplate";

import * as css from "./NavigationModal.module.css";

export interface INavigationModal extends IComponent {
    onClose?: (lastComponent: boolean) => boolean | void;
    onSuccess?: () => void;
    allowBodyScroll?: boolean;
}

interface INavigationModalState {
    page: number;
    isOpen: boolean;
}

export interface INavigationModalActionsField {
    userCommunicationTemplate?: IUserCommunicationTemplate;
}

export interface INavigationModalActions<
    TField extends INavigationModalActionsField = INavigationModalActionsField,
> {
    onSubmit?: (steps?: number) => boolean | void;
    previousPage?: (steps?: number, closePage?: boolean) => boolean | void;
    saveValues?: (data: TField) => void;
    onExit?: () => void;
    fieldValues?: TField;
    trackerAction?: string;
}

export function NavigationModal<
    TField extends INavigationModalActionsField,
    T extends INavigationModalActions<TField> = INavigationModalActions<TField>,
>(
    WrappedComponent: Array<React.ComponentType<T>>,
    wrappedProps: T[],
    initialData?: TField,
): React.ComponentType<INavigationModal> {
    return class extends Component<INavigationModal, INavigationModalState> {
        private fieldValues: TField | undefined = initialData || ({} as TField);

        private elem = createRef<HTMLDivElement>();

        constructor(props: INavigationModal) {
            super(props);
            this.state = {
                page: 0,
                isOpen: true,
            };
        }

        public componentDidMount() {
            const compProps = wrappedProps[this.state.page];

            requestTrack(compProps?.trackerAction);
        }

        public componentDidUpdate(_prevProps: INavigationModal, prevState: INavigationModalState) {
            if (this.state.page !== prevState.page) {
                requestTrack(wrappedProps[this.state.page].trackerAction);
            }
        }

        private setAnimation = () => {
            if (!this.elem.current) {
                return;
            }

            this.elem.current.animate({ opacity: ["1", "0"] } as PropertyIndexedKeyframes, {
                duration: 1000,
                fill: "forwards",
                direction: "reverse",
            });
        };

        private saveValues = (fields: TField) => {
            this.fieldValues = { ...this.fieldValues, ...fields };
        };

        private nextPage = (steps?: number) => {
            if (this.state.page < WrappedComponent.length - 1) {
                this.calculateNextStep(steps, 1);
            }
        };

        private previousPage = (steps?: number, closeModal?: boolean) => {
            if (closeModal === true) {
                this.setState({ isOpen: false, page: 0 });
                return;
            }

            if (this.state.page > 0) {
                this.calculateNextStep(steps, -1);
            } else {
                this.setState({ isOpen: false, page: 0 });
            }
        };

        private calculateNextStep = (steps: number | undefined, isNext: number) => {
            this.setAnimation();
            this.setState((prevState) => ({ page: prevState.page + isNext * (steps || 1) }));
        };

        private onClose = (): void => {
            if (this.props.onClose) {
                this.props.onClose(this.state.page === WrappedComponent.length - 1);
            }
        };

        public render() {
            if (WrappedComponent.length === 0) {
                return null;
            }

            if (
                this.state.page === WrappedComponent.length - 1 &&
                this.props.onSuccess &&
                this.fieldValues != null &&
                this.fieldValues.userCommunicationTemplate != null &&
                this.fieldValues.userCommunicationTemplate.displayType === DisplayType.Success
            ) {
                this.props.onSuccess();
            }

            const Comp = WrappedComponent[this.state.page];

            return (
                <Modal
                    clientId="info-modal"
                    isOpen={this.state.isOpen}
                    closeOnBlur={false}
                    closeOnEsc={false}
                    onClose={this.onClose}
                    className={css.navigationModalComponentModal}
                    allowBodyScroll={this.props.allowBodyScroll}
                    closeCustomTelemetry={{ eventName: "common-exit", actionType: "Click" }}
                >
                    <div ref={this.elem} {...defaultAttributes(this.props, css.navigationModalComponent)}>
                        <Comp
                            {...wrappedProps[this.state.page]}
                            fieldValues={this.fieldValues}
                            saveValues={this.saveValues}
                            previousPage={this.previousPage}
                            onExit={this.onClose}
                            onSubmit={this.nextPage}
                        />
                    </div>
                </Modal>
            );
        }
    };
}
